import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getInitials, formatHandicap } from '../utils/format';
import Icon from '../components/Icon';
import { useToast } from '../context/ToastContext';
import CourseAutocomplete from '../components/CourseAutocomplete';
import './Marketplace.css';

const RELATIONSHIP_LABELS = {
  self: { label: 'You', color: 'badge-navy' },
  connection: { label: '1st', color: 'badge-green' },
  '2nd_degree': { label: '2nd', color: 'badge-gray' },
};

function TeeTimeCard({ post, currentUserId, onStatusChange, toast }) {
  const navigate = useNavigate();
  const formatDate = (str) => new Date(str).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const formatTime = (str) => new Date(str).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const rel = RELATIONSHIP_LABELS[post.relationship] || RELATIONSHIP_LABELS['2nd_degree'];

  const postTypeLabel = {
    need_players: 'Needs Players',
    have_spots: 'Has Spots',
    have_time_open_spot: 'Open Spot',
    need_group: 'Needs Group',
  };

  const handleMessage = async () => {
    try {
      const conv = await api.getOrCreateDirectConversation(post.author_id);
      navigate(`/messages/${conv.id}`);
    } catch { toast.error('Failed to start conversation'); }
  };

  return (
    <div className="tt-card card">
      <div className="tt-card-header">
        <Link to={`/golfer/${post.author_id}`} className="tt-author">
          <div className="avatar avatar-sm">
            {post.author_photo ? <img src={post.author_photo} alt={post.author_name} /> : getInitials(post.author_name)}
          </div>
          <div>
            <p className="tt-author-name">{post.author_name}</p>
            <p className="tt-author-meta">
              {post.author_handicap != null && `Hdcp ${formatHandicap(post.author_handicap)}`}
            </p>
          </div>
        </Link>
        <div className="tt-badges">
          <span className={`badge ${rel.color}`}>{rel.label}</span>
          <span className="badge badge-orange">{postTypeLabel[post.post_type]}</span>
        </div>
      </div>

      <div className="tt-course">
        <div className="tt-course-text">
          <p className="tt-course-name">{post.course_name}</p>
          {(post.course_city || post.course_state) && (
            <p className="tt-course-loc">{[post.course_city, post.course_state].filter(Boolean).join(', ')}</p>
          )}
        </div>
        {post.spots_needed > 0 && (
          <span className="stamp">{post.spots_needed} {post.spots_needed === 1 ? 'spot' : 'spots'} open</span>
        )}
      </div>

      <div className="tt-stats">
        <div className="tt-stat">
          <span className="tt-stat-value">{formatDate(post.date_time)}</span>
          <span className="tt-stat-label">Date</span>
        </div>
        <div className="tt-stat">
          <span className="tt-stat-value">{formatTime(post.date_time)}</span>
          <span className="tt-stat-label">Tee Time</span>
        </div>
        <div className="tt-stat">
          <span className="tt-stat-value">{post.spots_needed}</span>
          <span className="tt-stat-label">Spots Needed</span>
        </div>
        {(post.handicap_min != null || post.handicap_max != null) && (
          <div className="tt-stat">
            <span className="tt-stat-value">
              {formatHandicap(post.handicap_min) ?? '—'} – {formatHandicap(post.handicap_max) ?? '—'}
            </span>
            <span className="tt-stat-label">Hdcp Range</span>
          </div>
        )}
      </div>

      {(post.gender_preference && post.gender_preference !== 'any') || post.age_min || post.age_max || post.requires_ghin ? (
        <div className="tt-filters">
          {post.requires_ghin && (
            <span className="badge badge-navy">GHIN Required</span>
          )}
          {post.gender_preference && post.gender_preference !== 'any' && (
            <span className="badge badge-gray" style={{ textTransform: 'capitalize' }}>{post.gender_preference} only</span>
          )}
          {(post.age_min || post.age_max) && (
            <span className="badge badge-gray">Age {post.age_min ?? '18'}–{post.age_max ?? '99'}</span>
          )}
        </div>
      ) : null}

      {post.description && <p className="tt-description">{post.description}</p>}

      <div className="tt-footer">
        {post.author_id === currentUserId ? (
          <div className="tt-owner-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => onStatusChange(post.id, 'filled')}>Mark Filled</button>
            <button className="btn btn-danger btn-sm" onClick={() => onStatusChange(post.id, 'closed')}>Close Post</button>
          </div>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={handleMessage}>
            <Icon name="chat" size={16} /> Message {post.author_name?.split(' ')[0]}
          </button>
        )}
      </div>
    </div>
  );
}

function CreateTeeTimeModal({ onClose, onCreate }) {
  const [courseName, setCourseName] = useState('');
  const [courseCity, setCourseCity] = useState('');
  const [courseState, setCourseState] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [spotsNeeded, setSpotsNeeded] = useState('1');
  const [handicapMin, setHandicapMin] = useState('');
  const [handicapMax, setHandicapMax] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [genderPreference, setGenderPreference] = useState('any');
  const [postType, setPostType] = useState('need_players');
  const [description, setDescription] = useState('');
  const [requiresGhin, setRequiresGhin] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!courseName || !date || !time) { setError('Course, date, and time are required'); return; }
    setSubmitting(true);
    try {
      // If this course didn't come from an existing suggestion, it was
      // typed/added via "Add it" — enrich the course directory for everyone.
      // Fire-and-forget: a failure here shouldn't block posting the tee time.
      if (!selectedCourseId) {
        api.addCourse({ name: courseName, city: courseCity || undefined, state: courseState || undefined }).catch(() => {});
      }
      const dateTime = new Date(`${date}T${time}`).toISOString();
      const post = await api.createTeeTimePost({
        courseName, courseCity, courseState, dateTime,
        spotsNeeded: parseInt(spotsNeeded),
        handicapMin: handicapMin ? parseFloat(handicapMin) : undefined,
        handicapMax: handicapMax ? parseFloat(handicapMax) : undefined,
        ageMin: ageMin ? parseInt(ageMin) : undefined,
        ageMax: ageMax ? parseInt(ageMax) : undefined,
        genderPreference, postType, description, requiresGhin,
      });
      onCreate(post);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card tt-modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Post a Tee Time</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close"><Icon name="x" size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form tt-modal-form">
          <div className="form-group">
            <label className="form-label">Post Type</label>
            <select className="form-input form-select" value={postType} onChange={e => setPostType(e.target.value)}>
              <option value="need_players">I have a tee time — need players</option>
              <option value="have_time_open_spot">I have a time with 1 open spot</option>
              <option value="need_group">Looking for a group to join</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Course Name</label>
            <CourseAutocomplete
              value={courseName}
              onChange={v => { setCourseName(v); setSelectedCourseId(null); }}
              onSelect={course => {
                setCourseName(course.name);
                setCourseCity(course.city || '');
                setCourseState(course.state || '');
                setSelectedCourseId(course.id);
              }}
              placeholder="Pebble Beach Golf Links"
              required
            />
          </div>

          <div className="modal-row">
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" value={courseCity} onChange={e => setCourseCity(e.target.value)} placeholder="Pebble Beach" />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input className="form-input" value={courseState} onChange={e => setCourseState(e.target.value)} placeholder="CA" maxLength={2} />
            </div>
          </div>

          <div className="modal-row">
            <div className="form-group">
              <label className="form-label">Date</label>
              <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Tee Time</label>
              <input className="form-input" type="time" value={time} onChange={e => setTime(e.target.value)} required />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Spots Needed</label>
            <select className="form-input form-select" value={spotsNeeded} onChange={e => setSpotsNeeded(e.target.value)}>
              {[1,2,3].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div className="tt-filter-section">
            <p className="tt-filter-label">Player Preferences (optional)</p>
            <div className="modal-row">
              <div className="form-group">
                <label className="form-label">Hdcp Min</label>
                <input className="form-input" type="number" step="0.1" value={handicapMin} onChange={e => setHandicapMin(e.target.value)} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Hdcp Max</label>
                <input className="form-input" type="number" step="0.1" value={handicapMax} onChange={e => setHandicapMax(e.target.value)} placeholder="36" />
              </div>
            </div>
            <div className="modal-row">
              <div className="form-group">
                <label className="form-label">Age Min</label>
                <input className="form-input" type="number" value={ageMin} onChange={e => setAgeMin(e.target.value)} placeholder="18" />
              </div>
              <div className="form-group">
                <label className="form-label">Age Max</label>
                <input className="form-input" type="number" value={ageMax} onChange={e => setAgeMax(e.target.value)} placeholder="65" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Gender Preference</label>
              <select className="form-input form-select" value={genderPreference} onChange={e => setGenderPreference(e.target.value)}>
                <option value="any">Any</option>
                <option value="male">Male only</option>
                <option value="female">Female only</option>
              </select>
            </div>
            <label className="form-checkbox-label" style={{ marginTop: 8 }}>
              <input type="checkbox" checked={requiresGhin} onChange={e => setRequiresGhin(e.target.checked)} />
              <span>Require GHIN number (ranked round)</span>
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Description (optional)</label>
            <textarea className="form-input form-textarea" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="e.g. Casual 18 holes, any pace welcome..." rows={2} />
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={submitting}>
            {submitting ? 'Posting...' : 'Post Tee Time'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Marketplace() {
  const { user } = useAuth();
  const toast = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filters, setFilters] = useState({ handicapMin: '', handicapMax: '', genderPreference: '', spotsMin: '' });

  const load = async () => {
    setLoading(true);
    try {
      const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      const data = await api.getMarketplace(activeFilters);
      setPosts(data);
    } catch (err) { toast.error('Failed to load tee times'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = (post) => { setPosts(prev => [post, ...prev]); };

  const handleStatusChange = async (postId, status) => {
    try {
      await api.updateTeeTimeStatus(postId, status);
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch { toast.error('Failed to update status'); }
  };

  const handleFilterChange = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  return (
    <div className="marketplace-page">
      <div className="marketplace-header">
        <div>
          <h1>Tee Times</h1>
          <p>Posts from your network — connections and 2nd-degree golfers</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <Icon name="plus" size={17} /> Post Tee Time
        </button>
      </div>

      {/* Filters */}
      <div className="marketplace-filters card">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label">Hdcp Range</label>
            <div className="filter-range">
              <input className="form-input filter-input" type="number" placeholder="Min" value={filters.handicapMin} onChange={e => handleFilterChange('handicapMin', e.target.value)} />
              <span className="filter-sep">–</span>
              <input className="form-input filter-input" type="number" placeholder="Max" value={filters.handicapMax} onChange={e => handleFilterChange('handicapMax', e.target.value)} />
            </div>
          </div>
          <div className="filter-group">
            <label className="form-label">Gender</label>
            <select className="form-input form-select filter-input" value={filters.genderPreference} onChange={e => handleFilterChange('genderPreference', e.target.value)}>
              <option value="">Any</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="form-label">Min Spots</label>
            <select className="form-input form-select filter-input" value={filters.spotsMin} onChange={e => handleFilterChange('spotsMin', e.target.value)}>
              <option value="">Any</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3</option>
            </select>
          </div>
          <button className="btn btn-navy filter-apply-btn" onClick={load}>Apply</button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container"><div className="spinner"></div></div>
      ) : posts.length > 0 ? (
        <div className="tt-list">
          {posts.map(post => (
            <TeeTimeCard key={post.id} post={post} currentUserId={user?.id} onStatusChange={handleStatusChange} toast={toast} />
          ))}
        </div>
      ) : Object.values(filters).some(v => v !== '') ? (
        <div className="empty-state card">
          <div className="empty-state-icon"><Icon name="search" size={24} /></div>
          <p className="empty-state-title">No tee times match your filters</p>
          <p className="empty-state-text">Try adjusting your handicap range, gender, or spot requirements to see more results.</p>
          <button className="btn btn-secondary" style={{ marginTop: 16 }} onClick={() => setFilters({ handicapMin: '', handicapMax: '', genderPreference: '', spotsMin: '' })}>
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="empty-state card">
          <div className="empty-state-icon"><Icon name="calendar" size={24} /></div>
          <p className="empty-state-title">No tee times in your network yet</p>
          <p className="empty-state-text">
            The marketplace shows posts from your connections and 2nd-degree golfers. Connect with more golfers to see their tee times — or be the first to post one!
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Post a Tee Time</button>
            <Link to="/search" className="btn btn-secondary">Find Golfers</Link>
          </div>
        </div>
      )}

      {showCreate && <CreateTeeTimeModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />}
    </div>
  );
}

export default Marketplace;
